import type { CSSProperties, JSX, ReactElement } from 'react';
import React from 'react';
import { ResponsiveContainer } from 'recharts';

import { Text } from '@feathr/components';

import * as styles from './TrendChartFrame.css';

export interface ITrendChartLegendEntry {
  label: string;
  color: string;
  /** When true, the swatch renders as a dashed ring instead of a filled dot. */
  dashed?: boolean;
}

export interface ITrendChartFrameProps {
  title: string;
  subtitle: string;
  /** `data-name` attribute on the frame root, used by tests to locate the chart. */
  dataName: string;
  /** `data-name` attribute on the legend wrapper. */
  legendDataName?: string;
  legend: ITrendChartLegendEntry[];
  /** The recharts chart element; wrapped in a `ResponsiveContainer` internally. */
  children: ReactElement;
}

/**
 * Shared wrapper for the Trends-tab charts (Views, Donation Amount, Donation
 * Count). Handles the heading block, responsive chart sizing, and legend
 * scaffolding so each chart component only declares its recharts series.
 */
function TrendChartFrame({
  title,
  subtitle,
  dataName,
  legendDataName,
  legend,
  children,
}: Readonly<ITrendChartFrameProps>): JSX.Element {
  return (
    <div className={styles.root} data-name={dataName}>
      <div className={styles.heading}>
        <Text.DefaultHeadingMedium>{title}</Text.DefaultHeadingMedium>
        <Text.SmallRegular>{subtitle}</Text.SmallRegular>
      </div>
      <div className={styles.chartContainer}>
        <ResponsiveContainer height={'100%'} width={'99%'}>
          {children}
        </ResponsiveContainer>
      </div>
      <div className={styles.legend} data-name={legendDataName}>
        {legend.map(({ label, color, dashed }) => (
          <div className={styles.legendItem} key={label}>
            <span
              className={dashed ? styles.legendSwatchDashed : styles.legendSwatch}
              style={{ '--p-swatch-color': color } as CSSProperties}
            />
            <Text inline={true} variant={'smallRegular'}>
              {label}
            </Text>
          </div>
        ))}
      </div>
    </div>
  );
}

export default TrendChartFrame;
